import { Box, Paper, TextField, Typography } from "@mui/material"
import moment from 'moment'
import { Day } from "./Day"
import { DaysContainer, RangeSelector } from "./styled"
import { DayOfWeek } from "./DayOfWeek"
import { useTranslation } from "react-i18next"
import { TimeRange } from "./types"
import { isDayInRange } from "./utils"
import { Asset, Button, ContentContainer } from '@shared/ui'
import { useState } from "react"
import { I18N_NAMESPACE } from "./i18n"

interface Props {
    currentDate: Date
    closeModal: () => void
    onChange: (timestamp?: number) => void
    withTime?: boolean
    range?: TimeRange
}

export function DatePickerWrapper(props: Props){
    const {
        currentDate,
        onChange,
        withTime,
        range,
        closeModal
    } = props

    const [selectedMonth, setSelectedMonth] = useState(moment(currentDate).valueOf())

    const { i18n, t } = useTranslation(I18N_NAMESPACE) 
    const lng = i18n.language.replace(/-.+$/, '')

    const firstDay = moment(selectedMonth).startOf('month')
    const endDay = moment(selectedMonth).endOf('month')
    const days = Array(endDay.daysInMonth())
        .fill(null)
        .map((_,i) => moment(firstDay).add(i, 'day'))
    
    return (
        <Paper
            elevation={4}
            sx={{
                padding: '15px',
                borderRadius: '16px'
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '40px auto 40px',
                    maxWidth: '100%'
                }}
            >
                <Button 
                    sx={{width: '40px', height: '40px'}}
                    onClick={() => setSelectedMonth(moment(selectedMonth).subtract(1, 'month').valueOf())}
                >
                    <Asset 
                        imgProps={{
                            style: {
                                rotate: '90deg', 
                                margin: '0 auto'
                            }
                        }}
                        namespace="icons" 
                        filename="dropdown.svg"
                        h={22}
                        w={22}
                    />
                </Button>
                <ContentContainer>
                    <Typography 
                        sx={{
                            m: '0 auto',
                            fontFamily: 'Rubik',
                            fontSize: '14px',
                            fontWeight: 700,
                            lineHeight: '16px'
                        }}
                    >
                        {new Date(currentDate).toLocaleDateString()}
                    </Typography>
                </ContentContainer>
                <Button 
                    sx={{width: '40px', height: '40px'}}
                    
                    onClick={() => setSelectedMonth(moment(selectedMonth).add(1, 'month').valueOf())}
                >
                    <Asset 
                        imgProps={{
                            style: {
                                rotate: '-90deg', 
                                margin: '0 auto'
                            }
                        }}
                        namespace="icons" 
                        filename="dropdown.svg"
                        h={22}
                        w={22}
                    />
                </Button>
            </Box>
            <Box
                sx={{
                    mt: '10px',
                    display: 'grid',
                    gridTemplateColumns: '40px auto 40px',
                    maxWidth: '100%'
                }}
            >
                <RangeSelector>
                    {t(`month.${moment(selectedMonth).month()}`)}
                </RangeSelector>
                <RangeSelector
                    sx={{
                        gridColumn: 3
                    }}
                >
                    {moment(selectedMonth).format('YYYY')}
                </RangeSelector>
            </Box>
            <DaysContainer
                sx={{
                    mt: '10px'
                }}
            >
                {new Array(7).fill(null).map((_, i) => <DayOfWeek key={'weekday-'+i} weekDayIndex={i}/>)}
            </DaysContainer>
            <Box sx={{
                mt: '10px',
                height: `${40*6 + 2*4}px`
            }}>
                <DaysContainer>
                    {
                        days.map((day, i) => (
                            <Day 
                                key={'day-button-'+i}
                                isDisabled={!isDayInRange(day, range)}
                                isSelected={moment(currentDate).isSame(day, 'day')}
                                onSelect={timestamp => {
                                    onChange(
                                        moment(timestamp)
                                            .hours(moment(currentDate).hours())
                                            .minutes(moment(currentDate).minutes())
                                            .valueOf()
                                    )
                                }}
                                date={day} 
                                offset={!i ? firstDay.locale(lng).weekday() + 1 : 0} 
                            />
                        ))
                    }
                </DaysContainer>
            </Box>
            {withTime && (
                <Box
                    sx={{
                        mt: '15px',
                        display: 'grid',
                        gridTemplateColumns: 'auto 40px auto'
                    }}
                >
                    <TextField
                        type="time"
                        label={t('time')}
                        variant="outlined"   
                        
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            },
                        }}
                        sx={{
                            '& .Mui-Input': {
                                color: 'red'
                            }
                        }}
                        onChange={(e) => {
                            const [hh, mm] = e.target.value.split(':')
                                .map(Number)
                                .filter(n => !Number.isNaN(n))
                            let m = moment(currentDate)
                            if(hh !== undefined) m = m.hours(hh)
                            if(mm !== undefined) m = m.minutes(mm)
                            onChange(m.valueOf())
                        }}
                        required
                        defaultValue={moment(currentDate).format('HH:mm')}
                    />
                </Box>
            )}
            <Button
                sx={{
                    mt: '10px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: 500
                }}
                onClick={() => closeModal()}
            >
                {t('actions.close')}
            </Button>
        </Paper>
    )
}